/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Paper, Skeleton, Typography } from "@mui/material";
import {
  AgricultureOutlined,
  ApartmentOutlined,
  BuildOutlined,
  CurrencyExchangeOutlined,
  GrassOutlined,
  PersonOutlined,
  TrendingUpOutlined,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useLazyGetTaxSystemConfigQuery } from "@api/applications";
import { useEffect, useState } from "react";
import { useLazyGetLoanUseCategoriesConfigQuery } from "@api/catalogs";
import { TaxSystemConfigResponse, UseLoanCategoryConfig } from "@interfaces/index";
import { amountToText, formatCurrency } from "@helpers/funciones";
import {
  ChoiceToggle,
  LinkTerminosPrivacidad,
  OptionCardGroup,
  PageHeader,
  SectionHeader,
  Button,
  SelectAnnotated,
} from "@components/ui_v2";
import { CardResumenCotizacion, SliderCotizador } from "@components/ui_v2/cotizadoresComponents";
import { TAX_SYSTEMS_CODES } from "@helpers/taxSystemsCodes";

const DEFAULT_DISCLAIMER =
  "Estimación informativa sujeta a aprobación del Comité de Crédito. Puede requerir garantías reales o la garantía natural del proyecto, conforme a los";

/** Código de tax system para cada combinación producto × régimen fiscal. */
const TAXSYSTEM_CODES: Record<ProductoId, Record<"PM" | "PFAE", string>> = {
  avio: {
    PM: TAX_SYSTEMS_CODES.PROVIDENCIA_AVIO_PM,
    PFAE: TAX_SYSTEMS_CODES.PROVIDENCIA_AVIO_PFAE,
  },
  refaccionario: {
    PM: TAX_SYSTEMS_CODES.PROVIDENCIA_REFACCIONARIO_PM,
    PFAE: TAX_SYSTEMS_CODES.PROVIDENCIA_REFACCIONARIO_PFAE,
  },
  cuenta_corriente: {
    PM: TAX_SYSTEMS_CODES.PROVIDENCIA_CUENTA_CORRIENTE_PM,
    PFAE: TAX_SYSTEMS_CODES.PROVIDENCIA_CUENTA_CORRIENTE_PFAE,
  },
  credito_simple: {
    PM: TAX_SYSTEMS_CODES.PROVIDENCIA_CREDITO_SIMPLE_PM,
    PFAE: TAX_SYSTEMS_CODES.PROVIDENCIA_CREDITO_SIMPLE_PFAE,
  },
};

/** Cultivos dummy para avío (siembra). Se reemplazarán con datos de la API. */
const CULTIVOS_SIEMBRA = [
  {
    value: "alfalfa_mant",
    label: "Alfalfa mantenimiento",
    desc: "PN 2026 · $20,000 por ha",
    costoPorHa: 48490,
    cuotaPorHa: 20000,
  },
  { value: "maiz_grano", label: "Maíz grano", desc: "PN 2026 · $18,000 por ha", costoPorHa: 42000, cuotaPorHa: 18000 },
  { value: "trigo", label: "Trigo", desc: "PN 2026 · $15,000 por ha", costoPorHa: 35000, cuotaPorHa: 15000 },
];

/** Actividades pecuarias dummy para avío (ganado). Se reemplazarán con datos de la API. */
const ACTIVIDADES_PECUARIAS = [
  {
    value: "bovino_carne_cria",
    label: "Bovino carne p-cría mantenimiento",
    desc: "PV 2026 · $10,000 por cabeza",
    costoPorCabeza: 16024,
    cuotaPorCabeza: 10000,
  },
  {
    value: "bovino_leche",
    label: "Bovino leche mantenimiento",
    desc: "PV 2026 · $12,000 por cabeza",
    costoPorCabeza: 18500,
    cuotaPorCabeza: 12000,
  },
  {
    value: "porcino",
    label: "Porcino engorda",
    desc: "PV 2026 · $8,000 por cabeza",
    costoPorCabeza: 12000,
    cuotaPorCabeza: 8000,
  },
];

const PRODUCTOS = [
  {
    id: "avio" as ProductoId,
    label: "Habilitación y/o Avío",
    sub: "Capital de trabajo por ciclo productivo",
    icon: <AgricultureOutlined />,
  },
  {
    id: "refaccionario" as ProductoId,
    label: "Refaccionario",
    sub: "Maquinaria, equipo, instalaciones y plantaciones",
    icon: <BuildOutlined />,
  },
  {
    id: "cuenta_corriente" as ProductoId,
    label: "Crédito Cuenta Corriente",
    sub: "Línea revolvente para tesorería",
    icon: <CurrencyExchangeOutlined />,
  },
  {
    id: "credito_simple" as ProductoId,
    label: "Crédito Simple",
    sub: "Apoyo al ciclo económico del negocio",
    icon: <TrendingUpOutlined />,
  },
];

const EMPTY_CONFIG: TaxSystemConfigResponse = {
  id: "",
  code: "",
  product_id: "",
  minAmount: 0,
  maxAmount: 0,
  stepAmount: 0,
  minTerm: 0,
  maxTerm: 0,
  stepTerm: 0,
  defaultAmount: 0,
  defaultTerm: 0,
  tax_system_type: "",
};

type ProductoId = "avio" | "refaccionario" | "cuenta_corriente" | "credito_simple";
type UsoAvio = "siembra" | "ganado";

// ─── Sub-componentes por producto ────────────────────────────────────────────

interface SeccionesAvioProps {
  categoriasUso: UseLoanCategoryConfig[];
  usoAvio: UsoAvio | null;
  setUsoAvio: (v: UsoAvio) => void;
  cultivoSeleccionado: string;
  setCultivoSeleccionado: (v: string) => void;
  superficieHa: number;
  setSuperficieHa: (v: number) => void;
  actividadPecuaria: string;
  setActividadPecuaria: (v: string) => void;
  cabezas: number;
  setCabezas: (v: number) => void;
}

interface SeccionesRefaccionarioProps {
  categoriasUso: UseLoanCategoryConfig[];
  destinoRefaccionario: string;
  setDestinoRefaccionario: (v: string) => void;
  cotizadorConfig: TaxSystemConfigResponse;
  isLoading: boolean;
  selectedAmount: number;
  setSelectedAmount: (v: number) => void;
  selectedTerm: number;
  setSelectedTerm: (v: number) => void;
  personType: "PM" | "PFAE";
}

const SeccionesAvio = ({
  categoriasUso,
  usoAvio,
  setUsoAvio,
  cultivoSeleccionado,
  setCultivoSeleccionado,
  superficieHa,
  setSuperficieHa,
  actividadPecuaria,
  setActividadPecuaria,
  cabezas,
  setCabezas,
}: SeccionesAvioProps) => (
  <>
    <SectionHeader variant="cotizador" label="¿Para qué vas a usarlo?" />
    <OptionCardGroup
      layout="grid"
      columns={2}
      iconShape="square"
      hideCheck
      value={usoAvio ?? ""}
      onChange={(v) => {
        setUsoAvio(v as UsoAvio);
        setCultivoSeleccionado("");
        setActividadPecuaria("");
      }}
      options={categoriasUso.map((c) => ({
        value: c.loan_use_categories.code,
        label: c.loan_use_categories.name,
        sub: c.loan_use_categories.description,
        icon: <GrassOutlined />,
        testId: `cotizador-providencia-uso-${c.loan_use_categories.code}`,
      }))}
    />
    {usoAvio === "siembra" && (
      <>
        <SectionHeader variant="cotizador" label="Cultivo" />
        <SelectAnnotated
          options={CULTIVOS_SIEMBRA}
          value={cultivoSeleccionado}
          onChange={setCultivoSeleccionado}
          id="cotizador-providencia-cultivo"
        />
      </>
    )}
    {usoAvio === "siembra" && cultivoSeleccionado && (
      <>
        <SectionHeader variant="cotizador" label="Superficie a habilitar" />
        <SliderCotizador
          value={superficieHa}
          onChange={setSuperficieHa}
          min={0.5}
          max={400}
          step={0.5}
          valueText={superficieHa % 1 === 0 ? `${superficieHa}` : superficieHa.toFixed(1)}
          unit="ha"
          minLabel="0.5 ha"
          maxLabel="400 ha"
          loading={false}
          testId="cotizador-providencia-superficie"
          valueAlign="center"
        />
      </>
    )}
    {usoAvio === "ganado" && (
      <>
        <SectionHeader variant="cotizador" label="Actividad pecuaria" />
        <SelectAnnotated
          options={ACTIVIDADES_PECUARIAS}
          value={actividadPecuaria}
          onChange={setActividadPecuaria}
          id="cotizador-providencia-actividad-pecuaria"
        />
      </>
    )}
    {usoAvio === "ganado" && actividadPecuaria && (
      <>
        <SectionHeader variant="cotizador" label="Cabezas a habilitar" />
        <SliderCotizador
          value={cabezas}
          onChange={setCabezas}
          min={1}
          max={800}
          step={1}
          valueText={String(cabezas)}
          unit="cabezas"
          minLabel="1 cabeza"
          maxLabel="800 cabezas"
          loading={false}
          testId="cotizador-providencia-cabezas"
          valueAlign="center"
        />
      </>
    )}
  </>
);

const SeccionesRefaccionario = ({
  categoriasUso,
  destinoRefaccionario,
  setDestinoRefaccionario,
  cotizadorConfig,
  isLoading,
  selectedAmount,
  setSelectedAmount,
  selectedTerm,
  setSelectedTerm,
  personType,
}: SeccionesRefaccionarioProps) => (
  <>
    <SectionHeader variant="cotizador" label="¿Qué vas a adquirir?" />
    <OptionCardGroup
      layout="grid"
      columns={3}
      iconShape="square"
      hideCheck
      value={destinoRefaccionario}
      onChange={setDestinoRefaccionario}
      options={categoriasUso.map((c) => ({
        value: c.loan_use_categories.code,
        label: c.loan_use_categories.name,
        sub: c.loan_use_categories.description,
        icon: <BuildOutlined />,
        testId: `cotizador-providencia-destino-${c.loan_use_categories.code}`,
      }))}
    />
    {destinoRefaccionario && (
      <>
        <SectionHeader
          variant="cotizador"
          label="Costo total del proyecto"
          meta={
            !isLoading && cotizadorConfig.minAmount > 0
              ? `${amountToText(cotizadorConfig.minAmount)} – ${amountToText(cotizadorConfig.maxAmount)}`
              : undefined
          }
        />
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2, mb: 1 }}>
          <Typography sx={{ fontSize: 20, color: "tokens.textSecondary" }}>$</Typography>
          <Box
            sx={{
              border: "1.5px solid",
              borderColor: "tokens.borderStrong",
              borderRadius: "10px",
              px: 3,
              py: "10px",
              minWidth: 200,
              textAlign: "center",
            }}
          >
            {isLoading ? (
              <Skeleton variant="text" width={160} height={36} />
            ) : (
              <Typography
                sx={{
                  fontSize: 28,
                  fontWeight: 600,
                  letterSpacing: "-0.02em",
                  fontVariantNumeric: "tabular-nums",
                  color: "primary.dark",
                }}
              >
                {selectedAmount > 0 ? selectedAmount.toLocaleString("es-MX") : "—"}
              </Typography>
            )}
          </Box>
          <Typography sx={{ fontSize: 16, fontWeight: 500, color: "tokens.textSecondary" }}>MXN</Typography>
        </Box>
        <SliderCotizador
          value={selectedAmount}
          onChange={setSelectedAmount}
          min={cotizadorConfig.minAmount}
          max={cotizadorConfig.maxAmount || 1}
          step={cotizadorConfig.stepAmount || 50000}
          minLabel={amountToText(cotizadorConfig.minAmount)}
          maxLabel={amountToText(cotizadorConfig.maxAmount)}
          loading={isLoading}
          testId="cotizador-providencia-monto-refaccionario"
        />
        {!isLoading && cotizadorConfig.maxAmount > 0 && (
          <Typography sx={{ fontSize: 12, color: "primary.main", mt: 1 }}>
            Financiamos hasta el 80% (máximo {formatCurrency(cotizadorConfig.maxAmount * 0.8)} MXN para{" "}
            {personType === "PM" ? "persona moral" : "persona física con actividad empresarial"}) · tu aportación mínima
            es del 20%.
          </Typography>
        )}
        <SectionHeader variant="cotizador" label="Plazo de pago" />
        {isLoading ? (
          <Skeleton variant="rounded" animation="wave" height={44} />
        ) : (
          <ChoiceToggle
            value={selectedTerm}
            onChange={(v) => setSelectedTerm(v as number)}
            options={Array.from(
              {
                length: Math.floor((cotizadorConfig.maxTerm - cotizadorConfig.minTerm) / cotizadorConfig.stepTerm) + 1,
              },
              (_, i) => cotizadorConfig.minTerm + i * cotizadorConfig.stepTerm,
            ).map((m) => ({ value: m, label: `${m} meses` }))}
            testId="cotizador-providencia-plazo-refaccionario"
          />
        )}
      </>
    )}
  </>
);

const SeccionesCreditoSimple = ({
  cotizadorConfig,
  isLoading,
  selectedAmount,
  setSelectedAmount,
  selectedTerm,
  setSelectedTerm,
}: {
  cotizadorConfig: TaxSystemConfigResponse;
  isLoading: boolean;
  selectedAmount: number;
  setSelectedAmount: (v: number) => void;
  selectedTerm: number;
  setSelectedTerm: (v: number) => void;
}) => (
  <>
    <SectionHeader
      variant="cotizador"
      label="Monto a solicitar"
      meta={
        !isLoading && cotizadorConfig.minAmount > 0
          ? `${amountToText(cotizadorConfig.minAmount)} – ${amountToText(cotizadorConfig.maxAmount)}`
          : undefined
      }
    />
    <SliderCotizador
      value={selectedAmount}
      onChange={setSelectedAmount}
      min={cotizadorConfig.minAmount}
      max={cotizadorConfig.maxAmount || 1}
      step={cotizadorConfig.stepAmount || 50000}
      valueText={selectedAmount > 0 ? formatCurrency(selectedAmount) : undefined}
      unit="MXN"
      minLabel={amountToText(cotizadorConfig.minAmount)}
      maxLabel={amountToText(cotizadorConfig.maxAmount)}
      loading={isLoading}
      testId="cotizador-providencia-monto"
    />
    <SectionHeader variant="cotizador" label="Plazo de pago" />
    {isLoading ? (
      <Skeleton variant="rounded" animation="wave" height={44} />
    ) : (
      <ChoiceToggle
        value={selectedTerm}
        onChange={(v) => setSelectedTerm(v as number)}
        options={Array.from(
          { length: Math.floor((cotizadorConfig.maxTerm - cotizadorConfig.minTerm) / cotizadorConfig.stepTerm) + 1 },
          (_, i) => cotizadorConfig.minTerm + i * cotizadorConfig.stepTerm,
        ).map((m) => ({ value: m, label: `${m} meses` }))}
        testId="cotizador-providencia-plazo"
      />
    )}
  </>
);

const SeccionesCuentaCorriente = ({
  cotizadorConfig,
  isLoading,
  selectedAmount,
  setSelectedAmount,
}: {
  cotizadorConfig: TaxSystemConfigResponse;
  isLoading: boolean;
  selectedAmount: number;
  setSelectedAmount: (v: number) => void;
}) => (
  <>
    <SectionHeader
      variant="cotizador"
      label="Línea de crédito que necesitas"
      meta={
        !isLoading && cotizadorConfig.minAmount > 0
          ? `${amountToText(cotizadorConfig.minAmount)} – ${amountToText(cotizadorConfig.maxAmount)}`
          : undefined
      }
    />
    <SliderCotizador
      value={selectedAmount}
      onChange={setSelectedAmount}
      min={cotizadorConfig.minAmount}
      max={cotizadorConfig.maxAmount || 1}
      step={cotizadorConfig.stepAmount || 50000}
      valueText={selectedAmount > 0 ? formatCurrency(selectedAmount) : undefined}
      unit="MXN"
      minLabel={amountToText(cotizadorConfig.minAmount)}
      maxLabel={amountToText(cotizadorConfig.maxAmount)}
      loading={isLoading}
      testId="cotizador-providencia-monto"
    />
  </>
);

// ─── Resumen de estimación (avío) ─────────────────────────────────────────────

const ResumenEstimacion = ({ filas }: { filas: { label: string; value: string }[] }) => (
  <Paper variant="lendia_paper" sx={{ mt: 2, display: "flex", flexDirection: "column", gap: "10px" }}>
    <Typography variant="body2" sx={{ fontWeight: 600, color: "primary.dark", mb: 0.5 }}>
      Resumen de tu estimación
    </Typography>
    {filas.map(({ label, value }) => (
      <Box key={label} sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
        <Typography sx={{ fontSize: 13, color: "tokens.textSecondary" }}>{label}</Typography>
        <Typography sx={{ fontSize: 13, fontWeight: 500, color: "primary.dark", flexShrink: 0 }}>{value}</Typography>
      </Box>
    ))}
  </Paper>
);

const CotizadorProvidencia = () => {
  const navigate = useNavigate();

  const [getTaxSystemConfig] = useLazyGetTaxSystemConfigQuery();
  const [getLoanUseCategoriesConfig] = useLazyGetLoanUseCategoriesConfigQuery();
  const [isLoading, setIsLoading] = useState(true);
  /** Respuestas de la API indexadas por código de tax system (ej. "CFP03"). */
  const [configuraciones, setConfiguraciones] = useState<Record<string, TaxSystemConfigResponse>>({});
  const [categoriasUso, setCategoriasUso] = useState<Record<string, UseLoanCategoryConfig[]>>({});
  /** Categorías de uso del crédito indexadas por código de tax system. */

  const [selectedProduct, setSelectedProduct] = useState<ProductoId | null>(null);
  const [personType, setPersonType] = useState<"PM" | "PFAE" | null>(null);

  // Estado exclusivo de avío
  const [usoAvio, setUsoAvio] = useState<UsoAvio | null>(null);
  const [cultivoSeleccionado, setCultivoSeleccionado] = useState("");
  const [superficieHa, setSuperficieHa] = useState(0.5);
  const [actividadPecuaria, setActividadPecuaria] = useState("");
  const [cabezas, setCabezas] = useState(1);

  // Estado exclusivo de refaccionario
  const [destinoRefaccionario, setDestinoRefaccionario] = useState("");

  // Resumen siembra (dummy — se reemplazará con lógica real)
  const cultivoActual = CULTIVOS_SIEMBRA.find((c) => c.value === cultivoSeleccionado) ?? CULTIVOS_SIEMBRA[0];
  const costoTotalSiembra = cultivoActual.costoPorHa * superficieHa;
  const aportacionSiembra = costoTotalSiembra - cultivoActual.cuotaPorHa * superficieHa;
  const pctAportacionSiembra = Math.round((aportacionSiembra / costoTotalSiembra) * 100);

  // Resumen ganado (dummy — se reemplazará con lógica real)
  const actividadActual = ACTIVIDADES_PECUARIAS.find((a) => a.value === actividadPecuaria) ?? ACTIVIDADES_PECUARIAS[0];
  const costoTotalGanado = actividadActual.costoPorCabeza * cabezas;
  const aportacionGanado = costoTotalGanado - actividadActual.cuotaPorCabeza * cabezas;
  const pctAportacionGanado = Math.round((aportacionGanado / costoTotalGanado) * 100);

  // const disclaimerText = selectedProduct ? DISCLAIMER_POR_PRODUCTO[selectedProduct] : undefined;
  const filasResumenSiembra = [
    { label: "Costo del paquete por ha", value: formatCurrency(cultivoActual.costoPorHa) },
    { label: "Cuota de crédito por ha", value: formatCurrency(cultivoActual.cuotaPorHa) },
    { label: "Costo total del proyecto", value: formatCurrency(costoTotalSiembra) },
    { label: "Aportación del productor", value: `${formatCurrency(aportacionSiembra)} (${pctAportacionSiembra}%)` },
  ];
  const filasResumenGanado = [
    { label: "Costo del paquete por cabeza", value: formatCurrency(actividadActual.costoPorCabeza) },
    { label: "Cuota de crédito por cabeza", value: formatCurrency(actividadActual.cuotaPorCabeza) },
    { label: "Costo total del proyecto", value: formatCurrency(costoTotalGanado) },
    { label: "Aportación del productor", value: `${formatCurrency(aportacionGanado)} (${pctAportacionGanado}%)` },
  ];
  const filasResumenAvio =
    usoAvio === "siembra" && cultivoSeleccionado
      ? filasResumenSiembra
      : usoAvio === "ganado" && actividadPecuaria
        ? filasResumenGanado
        : null;

  const montoAvio =
    usoAvio === "siembra"
      ? cultivoActual.cuotaPorHa * superficieHa
      : usoAvio === "ganado"
        ? actividadActual.cuotaPorCabeza * cabezas
        : 0;

  const mostrarResumenAvio =
    (usoAvio === "siembra" && !!cultivoSeleccionado) || (usoAvio === "ganado" && !!actividadPecuaria);
  const mostrarResumenRefaccionario = !!destinoRefaccionario;
  const [cotizadorConfig, setCotizadorConfig] = useState<TaxSystemConfigResponse>(EMPTY_CONFIG);
  const [selectedAmount, setSelectedAmount] = useState(0);
  const [selectedTerm, setSelectedTerm] = useState(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sourceCode = params.get("code");
    if (sourceCode) {
      sessionStorage.setItem("source_code", sourceCode);
    } else {
      sessionStorage.removeItem("source_code");
    }
  }, []);

  const fetchData = async () => {
    try {
      const uniqueCodes = [...new Set(Object.values(TAXSYSTEM_CODES).flatMap(({ PM, PFAE }) => [PM, PFAE]))];
      const codes_with_use_categories = uniqueCodes.filter((code) =>
        [
          TAX_SYSTEMS_CODES.PROVIDENCIA_AVIO_PM,
          TAX_SYSTEMS_CODES.PROVIDENCIA_AVIO_PFAE,
          TAX_SYSTEMS_CODES.PROVIDENCIA_REFACCIONARIO_PM,
          TAX_SYSTEMS_CODES.PROVIDENCIA_REFACCIONARIO_PFAE,
        ].includes(code),
      );
      const [responses, categorias] = await Promise.all([
        Promise.all(uniqueCodes.map((code) => getTaxSystemConfig(code).unwrap())),
        Promise.all(codes_with_use_categories.map((code) => getLoanUseCategoriesConfig({ taxCode: code }).unwrap())),
      ]);
      setConfiguraciones(Object.fromEntries(uniqueCodes.map((code, i) => [code, responses[i]])));
      setCategoriasUso(Object.fromEntries(codes_with_use_categories.map((code, i) => [code, categorias[i]])));
    } catch (error) {
      console.error("Error fetching tax system configs", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!selectedProduct || !personType) return;
    const code = TAXSYSTEM_CODES[selectedProduct][personType];
    const config = configuraciones[code];
    if (config) {
      setCotizadorConfig(config);
      setSelectedAmount(config.defaultAmount);
      setSelectedTerm(config.defaultTerm);
    }
  }, [selectedProduct, personType, configuraciones]);

  const setConditions = () => {
    if (!cotizadorConfig.product_id || !selectedAmount) return;

    const sourceCode = sessionStorage.getItem("source_code");
    let urlPortal = `/v2/producto/creditosimple/crear_cuenta?tax_system_id=${cotizadorConfig.id}&product_id=${cotizadorConfig.product_id}&person_type=${cotizadorConfig.code}&loan_amount=${selectedAmount}&loan_term=${selectedTerm}&tax_system_type=${cotizadorConfig.tax_system_type}`;
    console.log(urlPortal);
    if (sourceCode) {
      urlPortal += `&source_code=${sourceCode}`;
    }
    navigate(urlPortal);
  };

  return (
    <Box>
      <PageHeader
        variant="cotizador"
        eyebrow="COTIZACIÓN"
        title="Cotiza tu crédito"
        subtitle="Financiamiento para el campo, la agroindustria y las empresas rurales, con fondeo FIRA. Elige el producto y ajusta el proyecto para ver una estimación."
      />

      <Paper variant="lendia_paper">
        <SectionHeader variant="cotizador" first label="Producto de crédito" />

        <OptionCardGroup
          layout="grid"
          columns={2}
          iconShape="square"
          hideCheck={true}
          value={selectedProduct ?? ""}
          onChange={(v) => {
            setSelectedProduct(v as ProductoId);
            setPersonType(null);
            setUsoAvio(null);
            setCultivoSeleccionado("");
            setActividadPecuaria("");
            setDestinoRefaccionario("");
            setCotizadorConfig(EMPTY_CONFIG);
            setSelectedAmount(0);
            setSelectedTerm(0);
          }}
          options={PRODUCTOS.map((p) => ({
            value: p.id,
            label: p.label,
            sub: p.sub,
            icon: p.icon,
            testId: `cotizador-providencia-producto-${p.id}`,
          }))}
        />

        {/* — ¿Quién solicita? — visible al elegir producto ---------------------- */}
        {selectedProduct && (
          <>
            <SectionHeader variant="cotizador" label="¿Quién solicita el crédito?" />
            {isLoading ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Skeleton variant="rounded" animation="wave" height={64} />
              </Box>
            ) : (
              <OptionCardGroup
                layout="grid"
                columns={2}
                iconShape="square"
                hideCheck={true}
                value={personType ?? ""}
                onChange={(v) => setPersonType(v as "PM" | "PFAE")}
                options={[
                  {
                    value: "PM",
                    label: "Persona Moral",
                    sub: `Empresa o sociedad · crédito hasta ${amountToText(configuraciones[TAXSYSTEM_CODES[selectedProduct].PM]?.maxAmount ?? 0)}`,
                    icon: <ApartmentOutlined />,
                    testId: "cotizador-providencia-persona-moral",
                  },
                  {
                    value: "PFAE",
                    label: "Persona Física con Actividad Empresarial",
                    sub: `Actividad empresarial · crédito hasta ${amountToText(configuraciones[TAXSYSTEM_CODES[selectedProduct].PFAE]?.maxAmount ?? 0)}`,
                    icon: <PersonOutlined />,
                    testId: "cotizador-providencia-pfae",
                  },
                ]}
              />
            )}
          </>
        )}

        {/* — Secciones por producto + monto + plazo — visibles al elegir persona - */}
        {selectedProduct && personType && (
          <>
            {selectedProduct === "avio" && (
              <SeccionesAvio
                categoriasUso={categoriasUso[TAXSYSTEM_CODES[selectedProduct][personType]] ?? []}
                usoAvio={usoAvio}
                setUsoAvio={setUsoAvio}
                cultivoSeleccionado={cultivoSeleccionado}
                setCultivoSeleccionado={setCultivoSeleccionado}
                superficieHa={superficieHa}
                setSuperficieHa={setSuperficieHa}
                actividadPecuaria={actividadPecuaria}
                setActividadPecuaria={setActividadPecuaria}
                cabezas={cabezas}
                setCabezas={setCabezas}
              />
            )}
            {selectedProduct === "refaccionario" && (
              <SeccionesRefaccionario
                categoriasUso={categoriasUso[TAXSYSTEM_CODES[selectedProduct][personType]] ?? []}
                destinoRefaccionario={destinoRefaccionario}
                setDestinoRefaccionario={setDestinoRefaccionario}
                cotizadorConfig={cotizadorConfig}
                isLoading={isLoading}
                selectedAmount={selectedAmount}
                setSelectedAmount={setSelectedAmount}
                selectedTerm={selectedTerm}
                setSelectedTerm={setSelectedTerm}
                personType={personType!}
              />
            )}
            {selectedProduct === "credito_simple" && (
              <SeccionesCreditoSimple
                cotizadorConfig={cotizadorConfig}
                isLoading={isLoading}
                selectedAmount={selectedAmount}
                setSelectedAmount={setSelectedAmount}
                selectedTerm={selectedTerm}
                setSelectedTerm={setSelectedTerm}
              />
            )}
            {selectedProduct === "cuenta_corriente" && (
              <SeccionesCuentaCorriente
                cotizadorConfig={cotizadorConfig}
                isLoading={isLoading}
                selectedAmount={selectedAmount}
                setSelectedAmount={setSelectedAmount}
              />
            )}
          </>
        )}
      </Paper>

      {selectedProduct === "avio" && personType && mostrarResumenAvio && filasResumenAvio && (
        <ResumenEstimacion filas={filasResumenAvio} />
      )}

      {selectedProduct === "avio" && personType && mostrarResumenAvio && (
        <CardResumenCotizacion
          label="Monto a habilitar"
          amount={montoAvio > 0 ? formatCurrency(montoAvio) : "—"}
          caption="Capital de trabajo para el ciclo productivo"
          disclaimer={
            <>
              {DEFAULT_DISCLAIMER} <LinkTerminosPrivacidad />
            </>
          }
          actions={
            <Button
              tone="hero"
              withArrow
              onClick={setConditions}
              disabled={isLoading || montoAvio <= 0}
              data-testid="cotizador-providencia-continuar"
            >
              Continuar con mi solicitud
            </Button>
          }
        />
      )}

      {selectedProduct === "refaccionario" && personType && mostrarResumenRefaccionario && (
        <ResumenEstimacion
          filas={[
            { label: "Costo del proyecto (avalúo o cotización)", value: formatCurrency(selectedAmount) },
            { label: "Aportación mínima (20%)", value: formatCurrency(selectedAmount * 0.2) },
            {
              label: "Mensualidad estimada",
              value: (() => {
                const principal = selectedAmount * 0.8;
                const r = 0.18 / 12;
                return formatCurrency((principal * r) / (1 - Math.pow(1 + r, -selectedTerm)));
              })(),
            },
          ]}
        />
      )}

      {selectedProduct === "refaccionario" && personType && mostrarResumenRefaccionario && (
        <CardResumenCotizacion
          label="Monto a financiar"
          amount={selectedAmount > 0 ? formatCurrency(selectedAmount) : "—"}
          caption={
            selectedTerm > 0
              ? `Plazo de ${selectedTerm} meses · hasta el 80% del proyecto`
              : "Selecciona el plazo de pago"
          }
          disclaimer={
            <>
              {DEFAULT_DISCLAIMER} <LinkTerminosPrivacidad />
            </>
          }
          actions={
            <Button
              tone="hero"
              withArrow
              onClick={setConditions}
              disabled={isLoading || !cotizadorConfig.product_id || !selectedAmount}
              data-testid="cotizador-providencia-continuar"
            >
              Continuar con mi solicitud
            </Button>
          }
        />
      )}

      {selectedProduct === "credito_simple" && personType && (
        <CardResumenCotizacion
          label="Monto de crédito a solicitar"
          amount={selectedAmount > 0 ? formatCurrency(selectedAmount) : "—"}
          caption={`MXN · ${selectedTerm} meses · mensualidad estimada ${formatCurrency(selectedAmount / selectedTerm)}`}
          disclaimer={
            <>
              {DEFAULT_DISCLAIMER} <LinkTerminosPrivacidad />
            </>
          }
          actions={
            <Button
              tone="hero"
              withArrow
              onClick={setConditions}
              disabled={isLoading || !cotizadorConfig.product_id || !selectedAmount}
              data-testid="cotizador-providencia-continuar"
            >
              Continuar con mi solicitud
            </Button>
          }
        />
      )}

      {selectedProduct === "cuenta_corriente" && personType && (
        <CardResumenCotizacion
          label="Línea de crédito a solicitar"
          amount={selectedAmount > 0 ? formatCurrency(selectedAmount) : "—"}
          caption="MXN · línea revolvente · 12 meses renovable"
          disclaimer={
            <>
              Dispones y pagas cuantas veces lo necesites; el interés se calcula solo sobre el saldo dispuesto.
              Estimación informativa sujeta a aprobación del Comité de Crédito. Puede requerir garantías reales o la
              garantía natural del proyecto, conforme a los
              <LinkTerminosPrivacidad />
            </>
          }
          actions={
            <Button
              tone="hero"
              withArrow
              onClick={setConditions}
              disabled={isLoading || !cotizadorConfig.product_id || !selectedAmount}
              data-testid="cotizador-providencia-continuar"
            >
              Continuar con mi solicitud
            </Button>
          }
        />
      )}
    </Box>
  );
};

export default CotizadorProvidencia;
